import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "../../api/axiosInstance";

type Theme = {
    chat_header_gradient: string;
    user_msg_bg: string;
    bot_msg_bg: string;
    font: string;
    send_btn_gradient: string;
    toggle_bg: string;
};

const fonts = [
    "Poppins, sans-serif",
    "Roboto, sans-serif",
    "Open Sans, sans-serif",
    "Lato, sans-serif",
    "Montserrat, sans-serif"
];

const ChatbotThemeCustomizer = ({ restaurantId }: { restaurantId?: string }) => {
    if (!restaurantId)
    {
        restaurantId = localStorage.getItem("restaurant_id") || "";
    }
    const [theme, setTheme] = useState<Theme>({
        chat_header_gradient: '',
        user_msg_bg: '',
        bot_msg_bg: '',
        font: '',
        send_btn_gradient: '',
        toggle_bg: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (!restaurantId) {
            console.warn("Missing restaurant ID");
            return;
        }

        axios.get<Theme>(`/bot/theme/${restaurantId}`)
            .then(res => setTheme(res.data))
            .catch(err => {
                console.error("Error fetching theme:", err);
                if (err.response?.status === 401) navigate("/login");
            });
    }, [restaurantId]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setTheme({ ...theme, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        const token = localStorage.getItem("access_token");

        if (!token || !restaurantId) {
            console.error("Missing token or restaurant ID");
            return;
        }
        try {
            await axios.post(`/bot/theme`, {
                ...theme,
                restaurant_id: restaurantId
            });
            alert("Theme saved!");
        } catch (error: any) {
            console.error("Error saving theme:", error);
            alert(error.response?.data?.error || "Failed to save theme.");
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">🎨 Customize Chatbot Theme</h2>

            <div className="mb-6 p-4 rounded border" style={{ fontFamily: theme.font }}>
                <div style={{ background: theme.chat_header_gradient, color: '#fff', padding: '10px', borderRadius: '6px' }}>
                    <strong>Chat Header Preview</strong>
                </div>
                <div style={{ background: theme.bot_msg_bg, color: '#fff', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>
                    Hello! I'm your food assistant 🍔
                </div>
                <div style={{ background: theme.user_msg_bg, color: '#fff', padding: '10px', borderRadius: '6px', marginTop: '8px', textAlign: 'right' }}>
                    Recommend me lunch
                </div>
            </div>

            <div className="mb-6">
                <label className="block font-medium mb-2">Toggle Button Preview</label>
                <div
                    style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '9999px',
                        background: theme.toggle_bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        color: '#fff',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    }}
                >
                    💬
                </div>
            </div>

            <div className="mb-6">
                <label className="block font-medium mb-2">Send Button Preview</label>
                <button
                    style={{
                        background: theme.send_btn_gradient,
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        fontWeight: "bold",
                        border: "none",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                        cursor: "pointer"
                    }}
                >
                    Get Recommendation
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block font-medium">Header Gradient</label>
                    <input type="text" name="chat_header_gradient" value={theme.chat_header_gradient} onChange={handleChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                    <label className="block font-medium">User Message Color</label>
                    <input type="color" name="user_msg_bg" value={theme.user_msg_bg} onChange={handleChange} className="w-full h-10 p-1 rounded" />
                </div>

                <div>
                    <label className="block font-medium">Bot Message Color</label>
                    <input type="color" name="bot_msg_bg" value={theme.bot_msg_bg} onChange={handleChange} className="w-full h-10 p-1 rounded" />
                </div>

                <div>
                    <label className="block font-medium">Send Button Gradient</label>
                    <input type="text" name="send_btn_gradient" value={theme.send_btn_gradient} onChange={handleChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                    <label className="block font-medium">Toggle Button Gradient</label>
                    <input type="text" name="toggle_bg" value={theme.toggle_bg} onChange={handleChange} className="w-full p-2 border rounded" />
                </div>

                <div>
                    <label className="block font-medium">Font</label>
                    <select name="font" value={theme.font} onChange={handleChange} className="w-full p-2 border rounded">
                        {fonts.map(f => (
                            <option key={f} value={f}>{f}</option>
                        ))}
                    </select>
                </div>
            </div>

            <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 mt-6 rounded hover:bg-blue-700">
                Save Theme
            </button>
        </div>
    );
};

export default ChatbotThemeCustomizer;
